import { PaytrieAPI } from '@paytrie/client'
import { NetworkValue, StableCoinValue } from '@/utils/currencies'

export type TransactionSuccess = {
  data: { tx: string; rmtId: string | null; depositAddress: string | null }
}

export type TransactionFailure = {
  status: number
  message: string
  responseBody?: string
}

type SubmitResult =
  | { response: TransactionSuccess }
  | { error: TransactionFailure }

const MAX_BODY_CHARS = 1000

const truncateBody = (data: unknown): string => {
  try {
    const serialized = JSON.stringify(data)
    return serialized.length > MAX_BODY_CHARS
      ? `${serialized.slice(0, MAX_BODY_CHARS)}…`
      : serialized
  } catch {
    return String(data).slice(0, MAX_BODY_CHARS)
  }
}

type BuyPayload = {
  quoteId: number
  wallet: string
  amount: number
  stableCoin: StableCoinValue
  network: NetworkValue
  useReferral?: boolean
}

type SellPayload = {
  quoteId: number
  wallet: string
  amount: number
  stableCoin: StableCoinValue
  network: NetworkValue
  useReferral?: boolean
}

const firstErrorMessage = (data: unknown): string => {
  if (
    data &&
    typeof data === 'object' &&
    'errors' in data &&
    Array.isArray((data as { errors: unknown }).errors)
  ) {
    const first = (data as { errors: { message?: unknown }[] }).errors[0]
    if (first && typeof first.message === 'string') return first.message
  }
  return ''
}

const submitTransaction = async (
  payload: PaytrieAPI.CreateTransactionRequest
): Promise<SubmitResult> => {
  try {
    const externalSessionId = sessionStorage.getItem('externalSessionId')
    const response = await PaytrieAPI.createTransaction({
      ...payload,
      ...(externalSessionId ? { externalSessionId } : {}),
    })

    if (response.status === 201 && response.data.success) {
      return {
        response: {
          data: {
            tx: response.data.data.id,
            rmtId: response.data.data.rmtId ?? null,
            depositAddress: response.data.data.depositAddress ?? null,
          },
        },
      }
    }

    return {
      error: {
        status: response.status,
        message: firstErrorMessage(response.data),
        responseBody: truncateBody(response.data),
      },
    }
  } catch (e) {
    return {
      error: {
        status: 500,
        message: 'Unknown error occurred submitting transaction',
        responseBody:
          e instanceof Error ? `${e.name}: ${e.message}` : String(e),
      },
    }
  }
}

type SideLabel = PaytrieAPI.CreateTransactionRequest['leftSideLabel']

export const submitBuy = (buyPayload: BuyPayload): Promise<SubmitResult> =>
  submitTransaction({
    leftSideLabel: 'CAD',
    leftSideValue: buyPayload.amount,
    rightSideLabel:
      `${buyPayload.stableCoin}-${buyPayload.network}` as SideLabel,
    wallet: buyPayload.wallet,
    quoteId: buyPayload.quoteId,
    ...(buyPayload.useReferral ? { useReferral: true } : {}),
  })

export const submitSell = (payload: SellPayload): Promise<SubmitResult> =>
  submitTransaction({
    leftSideLabel: `${payload.stableCoin}-${payload.network}` as SideLabel,
    leftSideValue: payload.amount,
    rightSideLabel: 'CAD',
    wallet: payload.wallet,
    quoteId: payload.quoteId,
    ...(payload.useReferral ? { useReferral: true } : {}),
  })
